<template>
  <div class="min-h-screen flex flex-col bg-gray-50">

    <!-- Hero Section with Login -->
    <section
      class="bg-gradient-to-b from-[#14336b] to-blue-500 text-white min-h-screen md:min-h-0 py-0 md:py-16 relative overflow-hidden flex flex-col justify-center"
      aria-label="Área principal de login">

      <!-- CANVAS DE ONDAS -->
      <canvas ref="waveCanvas" class="absolute top-0 left-0 w-full h-full z-0 pointer-events-none"
        aria-hidden="true"></canvas>

      <div
        class="container mx-auto px-4 md:px-6 flex flex-col md:flex-row items-center justify-center gap-10 relative z-10 -mt-2 md:mt-0">

        <!-- Bloco Esquerdo -->
        <div class="md:w-1/2 text-center md:text-left justify-center items-start flex flex-col">
          <img :src="Logo" alt="Logo do Portal do Paciente - Acesso a exames e resultados"
            class="w-40 md:w-[300px] h-auto mb-5 mx-auto md:mx-0" />

          <img :src="LogoSaudePublicaCarioca" alt="Logo da Saúde Pública Carioca - Secretaria Municipal de Saúde"
            class="w-[430px] md:w-[430px] h-auto" />
        </div>

        <!-- Bloco Direito (Login Form) -->
        <div class="md:w-1/2 bg-white p-6 md:p-8 rounded-lg shadow-lg w-full max-w-md">
          <img
            src="https://cdn.greatsoftwares.com.br/arquivos/paginas_editor/71316-805160676f7bac99a0b6f65acb37f591.png"
            alt="" class="w-48 md:w-80 mx-auto my-3" aria-hidden="true" />

          <form @submit.prevent="handleLogin" aria-labelledby="loginFormTitle">
            <h2 id="loginFormTitle" class="sr-only">Formulário de acesso ao portal do paciente</h2>

            <div class="mb-4">
              <label for="login" class="block text-gray-700 mb-2">Login Exame</label>
              <input type="text" id="login" v-model="loginForm.login"
                class="w-full px-4 text-gray-700 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#005baa]"
                placeholder="Digite seu Login" required aria-describedby="loginHelp" autocomplete="username" />
              <p id="loginHelp" class="sr-only">Utilize o Login fornecido no seu protocolo do paciente</p>
            </div>

            <div class="mb-4 relative">
              <label for="password" class="block text-gray-700 mb-2">Senha</label>
              <input :type="showPassword ? 'text' : 'password'" id="password" v-model="loginForm.password"
                class="w-full text-gray-700 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#005baa] pr-12"
                placeholder="Digite sua senha" required aria-describedby="passwordHelp"
                autocomplete="current-password" />
              <p id="passwordHelp" class="sr-only">Utilize a senha fornecido no seu protocolo do paciente</p>

              <button type="button" @click="showPassword = !showPassword"
                class="absolute top-[42px] right-3 text-gray-500 hover:text-[#005baa] focus:outline-none focus:ring-2 focus:ring-[#005baa]"
                :aria-label="showPassword ? 'Ocultar senha' : 'Revelar senha'" :aria-describedby="'password'">
                <component :is="showPassword ? EyeOff : Eye" class="w-5 h-5" aria-hidden="true" />
              </button>
            </div>

            <div class="mb-4">
              <div ref="recaptchaEl" id="recaptcha-container" role="alert" aria-live="polite"
                aria-label="Verificação de segurança - Por favor complete o desafio reCAPTCHA"></div>
            </div>

            <p v-if="errorMessage" role="alert" class="text-sm text-red-600 mb-2">{{ errorMessage }}</p>

            <button type="submit"
              class="w-full bg-[#005baa] text-white py-3 rounded-md hover:bg-[#004a8c] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#005baa]"
              :disabled="isLoading" :aria-disabled="isLoading">
              <span v-if="isLoading" class="flex items-center justify-center">
                <span class="sr-only">Processando credenciais</span>
                <svg class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none"
                  viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Carregando...
              </span>
              <span v-else>Acessar resultados</span>
            </button>

          </form>
        </div>
      </div>

      <!-- Scroll indicator -->
      <!-- Scroll indicator -->
      <div class="absolute bottom-2 inset-x-0 z-10 animate-bounce flex justify-center">
        <button @click="scrollToNextSection"
          class="focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffffff33] focus-visible:ring-offset-2"
          aria-label="Role para baixo para próxima seção disponível" aria-describedby="scrollHint">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-7 w-7 text-white opacity-80" fill="none" viewBox="0 0 24 24"
            stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
          <span id="scrollHint" class="sr-only">Esta seta indica conteúdo adicional abaixo</span>
        </button>
      </div>

    </section>


    <!-- Informes Section -->
    <section v-show="informes.length > 0" id="informes" class="py-12 bg-white relative"
      aria-labelledby="informes-heading">
      <div class="container mx-auto px-4 md:px-6 relative">
        <h2 id="informes-heading" class="text-2xl md:text-3xl font-bold text-[#005baa] mb-8 text-center">
          Notícias e Novidades
        </h2>

        <Swiper :modules="[Pagination, Autoplay]" :slides-per-view="1" :space-between="20" :loop="true"
          :autoplay="{ delay: 5000, disableOnInteraction: false }" :pagination="{ clickable: true }" :breakpoints="{
            640: { slidesPerView: 1 },
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 }
          }" class="pb-10 relative" aria-label="Carrossel de informes e novidades">
          <SwiperSlide v-for="(informe, index) in informes" :key="index"
            class="bg-gray-50 rounded-lg shadow-md overflow-hidden flex flex-col">
            <!-- Imagem -->
            <div class="relative w-full flex items-center justify-center">
              <div class="relative">
                <img :src="informe.imagem_base64" :alt="'Imagem do informe: ' + informe.titulo"
                  class="max-h-[300px] w-auto object-contain mx-auto block rounded-lg" />
                <div
                  class="hidden absolute top-2 right-2 bg-[#005baa] text-white text-xs px-3 py-1 rounded-lg shadow z-10"
                  aria-label="Data de publicação">
                  {{ formatarData(informe.data_publicacao) }}
                </div>
              </div>
            </div>

            <!-- Conteúdo -->
            <div class="p-4 flex flex-col justify-between flex-grow">
              <div>
                <h3 class="text-lg font-semibold text-[#005baa] mb-2 line-clamp-2">
                  {{ informe.titulo }}
                </h3>

              </div>

              <!-- Botão Leia mais -->
              <div v-if="informe.conteudo" class="text-right mt-auto">
                <button @click="abrirVisualizacao(informe)"
                  class="text-[#005baa] font-medium hover:underline text-sm focus:outline-none focus:ring-2 focus:ring-[#005baa] focus:ring-offset-2 rounded-md px-2 py-1"
                  aria-label="Leia mais sobre este informe">
                  Leia mais
                </button>
              </div>
            </div>
          </SwiperSlide>
        </Swiper>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-12 bg-white" aria-labelledby="faq-heading">
      <div class="container mx-auto px-4 md:px-6">
        <h2 id="faq-heading" class="text-2xl md:text-3xl font-bold text-[#005baa] mb-8 text-center">Perguntas Frequentes
        </h2>

        <div class="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <div v-for="(faq, index) in faqs" :key="index">
            <!-- Card FAQ -->
            <div class="bg-[#003b6f] rounded-lg shadow-sm transition-all overflow-hidden">
              <button @click="toggleFaq(index)"
                class="w-full text-left p-4 flex justify-between items-center cursor-pointer hover:bg-[#2788d8] transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#003b6f]"
                :aria-expanded="faq.isOpen" :aria-controls="`faq-answer-${index}`">
                <h3 class="font-medium text-white">{{ faq.question }}</h3>
                <ChevronDown :class="{ 'transform rotate-180': faq.isOpen }"
                  class="h-5 w-5 text-white transition-transform duration-200" aria-hidden="true" />
              </button>

              <transition name="fade">
                <div v-if="faq.isOpen" class="p-4 border-t border-gray-100 bg-[#005baa]" :id="`faq-answer-${index}`">
                  <p class="text-white text-sm leading-relaxed whitespace-pre-line">
                    {{ faq.answer }}
                  </p>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Adicione esta nova seção antes do Footer -->
    <section class="py-12 bg-gray-50" aria-labelledby="location-heading">
      <div class="container mx-auto px-4 md:px-6">
        <h2 id="location-heading" class="text-2xl md:text-3xl font-bold text-[#005baa] mb-8 text-center">Nossa
          Localização</h2>

        <div class="max-w-4xl mx-auto">
          <!-- Mapa do Google com título e descrição para acessibilidade -->
          <div class="rounded-lg overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3675.4572076486543!2d-43.2411986238549!3d-22.896501179262255!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x997e8e15ac08d9%3A0x5c01611842fb1c5f!2sR.%20Dr.%20Rodrigues%20de%20Santana%2C%2053%20-%20Benfica%2C%20Rio%20de%20Janeiro%20-%20RJ%2C%2020910-240!5e0!3m2!1sen!2sbr!4v1720121062511!5m2!1sen!2sbr"
              width="100%" height="450" style="border:0;" allowfullscreen="" loading="lazy"
              referrerpolicy="no-referrer-when-downgrade"
              title="Localização do Centro Carioca de Diagnóstico e Tratamento de Imagem"
              aria-label="Mapa mostrando a localização do Centro Carioca de Diagnóstico e Tratamento de Imagem na Rua Dr. Rodrigues de Santana, 53 - Benfica, Rio de Janeiro">
            </iframe>
          </div>
          <!-- Adicionando informações de endereço em texto para melhor acessibilidade-->
          <div class="mt-6 text-center">
            <p class="text-gray-700">
              <strong>Endereço:</strong> Rua Dr. Rodrigues de Santana, 53 - Benfica, Rio de Janeiro - RJ,
              20910-240
            </p>
            <p class="text-gray-700 mt-2">
              <strong>Horário de funcionamento:</strong> Segunda a sexta, das 7h às 20h e sábado, das 8h às 20h
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-gradient-to-b from-[#14336b] to-blue-900 text-white py-2 mt-auto" role="contentinfo">
      <div class="container mx-auto px-4 md:px-6">
        <!-- Logo centralizada -->
        <div class="flex justify-center mb-5">
          <img :src=LogoCircleWhite alt="Logo do Centro Carioca de Diagnóstico e Tratamento de Imagem"
            class="w-[620px] md:w-[620px] h-auto" />
        </div>

        <!-- Rodapé final -->
        <div class="pt-2 text-center text-sm">
          <p>&copy; {{ new Date().getFullYear() }} Desenvolvido por Tecnologia da Informação - CCDTI © Todos os direitos
            reservados</p>
        </div>
      </div>
    </footer>
  </div>

  <!-- View Announcement Modal -->
  <transition name="fade">
    <div v-if="modalVisualizacao" role="dialog" aria-modal="true" aria-labelledby="modal-title"
      class="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      @click.self="modalVisualizacao = false" @keydown.esc="modalVisualizacao = false">
      <div
        class="bg-white  w-full max-w-3xl rounded-2xl shadow-2xl border border-gray-200  overflow-hidden animate-fadeIn flex flex-col"
        tabindex="-1">
        <!-- Cabeçalho -->
        <div class="flex justify-between items-center p-5 border-b border-gray-200">
          <h3 id="modal-title" class="text-2xl font-bold text-[#005baa] "></h3>
          <button @click="modalVisualizacao = false"
            class="text-gray-600  hover:text-red-500 transition focus:outline-none focus:ring-2 focus:ring-[#005baa]"
            aria-label="Fechar modal">
            <X class="w-6 h-6" aria-hidden="true" />
          </button>
        </div>

        <!-- Corpo -->
        <div class="p-6 space-y-6 max-h-[70vh] overflow-y-auto flex-grow">
          <!-- Imagem (proporcional, sem bordas cinzas e data sobre a imagem) -->
          <div v-if="informeSelecionado && informeSelecionado.imagem_base64"
            class="relative w-full flex items-center justify-center">
            <div class="relative">
              <img :src="informeSelecionado.imagem_base64" :alt="`Imagem do informe: ${informeSelecionado.titulo}`"
                class="max-h-[300px] w-auto object-contain mx-auto block rounded-lg" />
              <div class="hidden absolute top-2 right-2 bg-[#005baa] text-white text-xs px-3 py-1 rounded-lg shadow z-10"
                aria-label="Data de publicação">
                {{ formatarData(informeSelecionado.data_publicacao) }}
              </div>
            </div>
          </div>

          <!-- Título -->
          <h2 class="text-xl font-bold text-[#005baa] ">
            {{ informeSelecionado?.titulo || '' }}
          </h2>

          <!-- Conteúdo -->
                    <!-- Conteúdo utilizando QuillContent -->
                    <div v-if="informeSelecionado && informeSelecionado.conteudo">
            <QuillContent :conteudo="informeSelecionado.conteudo"/>
          </div>
        </div>

        <!-- Rodapé com botão fechar -->
        <div class="px-6 py-4 border-t border-gray-200  flex justify-end">
          <button @click="modalVisualizacao = false"
            class="bg-[#005baa] hover:bg-[#004a8c] text-white text-sm font-medium px-6 py-2 rounded-md transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#005baa]"
            aria-label="Fechar detalhes do informe">
            Fechar
          </button>
        </div>
      </div>
    </div>
  </transition>


  
  <!-- VLibras Plugin de Acessibilidade -->
  <div vw class="enabled">
    <div vw-access-button class="active" aria-label="Ativar VLibras"></div>
    <div vw-plugin-wrapper>
      <div class="vw-plugin-top-wrapper"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import { ChevronDown, Eye, EyeOff, X } from 'lucide-vue-next';
import QuillContent from '@/components/QuillContent.vue';
import Logo from '@/assets/Logo.png';
import LogoSaudePublicaCarioca from '@/assets/LogoSaudePublicaCarioca.png';
import LogoCircleWhite from '@/assets/LogoCircleWhite.png';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const waveCanvas = ref(null);
const showPassword = ref(false);

const loginForm = ref({
  login: '',
  password: ''
});
const isLoading = ref(false);
const errorMessage = ref('');
const informes = ref([]);
const recaptchaEl = ref(null);
let recaptchaWidgetId = null;

const modalVisualizacao = ref(false);
const mostrarConteudoCompleto = ref(false);
const informeSelecionado = ref({});

// Abrir modal com dados
const abrirVisualizacao = (informe) => {
  informeSelecionado.value = informe;
  mostrarConteudoCompleto.value = true; // sempre expandido
  modalVisualizacao.value = true;

  // Foco no modal para acessibilidade
  nextTick(() => {
    const modalElement = document.querySelector('[role="dialog"]');
    if (modalElement) {
      const focusableElement = modalElement.querySelector('button');
      if (focusableElement) focusableElement.focus();
    }
  });
};

// Gerenciar foco quando o modal é aberto
onMounted(() => {
  // Adicionar listener para tecla Tab para manter o foco dentro do modal
  window.addEventListener('keydown', trapFocus);
});

onUnmounted(() => {
  window.removeEventListener('keydown', trapFocus);
});

const trapFocus = (e) => {
  if (!modalVisualizacao.value) return;

  // Se a tecla pressionada não for Tab, retornar
  if (e.key !== 'Tab') return;

  const modal = document.querySelector('[role="dialog"]');
  if (!modal) return;

  const focusableElements = modal.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );

  if (focusableElements.length === 0) return;

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  // Se Shift+Tab e o primeiro elemento está focado, mover para o último
  if (e.shiftKey && document.activeElement === firstElement) {
    e.preventDefault();
    lastElement.focus();
  }
  // Se Tab e o último elemento está focado, mover para o primeiro
  else if (!e.shiftKey && document.activeElement === lastElement) {
    e.preventDefault();
    firstElement.focus();
  }
};

onMounted(() => {
  const waitForRecaptcha = setInterval(() => {
    if (window.grecaptcha && window.grecaptcha.render) {
      recaptchaWidgetId = window.grecaptcha.render(recaptchaEl.value, {
        sitekey: '6LdLPSAqAAAAAFu3ZAsx1duJOLZh4tEWXNXp14pe'
      });
      clearInterval(waitForRecaptcha);
    }
  }, 300);

  // Adicionar skip link para acessibilidade
  const skipLink = document.createElement('a');
  skipLink.href = '#login'; // Agora direciona para o campo de login
  skipLink.className = 'sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-white text-[#005baa] p-2 z-50 rounded';
  skipLink.textContent = 'Pular para o campo de login';
  document.body.insertBefore(skipLink, document.body.firstChild);

});

const handleLogin = async () => {
  let redirected = false;
  errorMessage.value = '';

  const recaptchaResponse = window.grecaptcha?.getResponse(recaptchaWidgetId);
  if (!recaptchaResponse) {
    errorMessage.value = 'Por favor, complete o reCAPTCHA.';
    return;
  }

  isLoading.value = true;

  const data = new URLSearchParams();
  data.append('loginUsuario', loginForm.value.login);
  data.append('loginSenha', loginForm.value.password);
  data.append('g-recaptcha-response', recaptchaResponse);

  // Cria um AbortController para cancelar a requisição se necessário
  const controller = new AbortController();
  const signal = controller.signal;

  // Função para abortar a requisição se a aba ficar oculta
  const handleVisibilityChange = () => {
    if (document.hidden) {
      controller.abort();
    }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);

  try {
    const response = await fetch('https://portal.ccdti.com.br/Portal/auth', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: data.toString(),
      credentials: 'include',
      signal
    });

    const result = await response.json();

    if (result.length > 0 && result[0].status === "1") {
      const redirectUrl = `https://portal.ccdti.com.br/Portal/auth?loginUsuario=${encodeURIComponent(loginForm.value.login)}&loginSenha=${encodeURIComponent(loginForm.value.password)}`;
      window.location.href = redirectUrl;
      redirected = true;
    } else {
      errorMessage.value = 'Login ou senha inválidos. Tente novamente.';
      // Limpa os campos de login após erro
      loginForm.value.login = '';
      loginForm.value.password = '';
      // Foco no campo de erro para leitores de tela
      nextTick(() => {
        document.getElementById('login').focus();
      });
    }
  } catch (err) {
    if (err.name === 'AbortError') {
      errorMessage.value = 'Processo abortado por mudança de aba.';
    } else {
      errorMessage.value = 'Erro ao conectar com o servidor.';
      console.error(err);
    }
  } finally {
    // Remove o listener para evitar vazamentos de memória
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    
    // Se não houve redirecionamento, limpa o estado e reseta o reCAPTCHA.
    if (!redirected) {
      isLoading.value = false;
      window.grecaptcha?.reset(recaptchaWidgetId);
    }
  }
};



// Wave animation variables
let ctx;
let width;
let height;
let waves = [];
let animationFrameId;
let lastTime = 0;

// Initialize wave animation
const initWaveAnimation = () => {
  if (!waveCanvas.value) return;

  ctx = waveCanvas.value.getContext('2d');
  resizeCanvas();

  // Create waves with different properties
  waves = [
    createWave(0.02, 0.1, 2, '#024588', 0.3),
    createWave(0.03, 0.15, 2, '#024588', 0.2),
    createWave(0.01, 0.10, 2, '#024588', 0.4)
  ];

  // Start animation loop
  animateWaves();

  // Handle window resize
  window.addEventListener('resize', resizeCanvas);
};

// Create a wave with specific properties
const createWave = (frequency, amplitude, speed, color, opacity) => {
  return { frequency, amplitude, speed, color, opacity, offset: 0 };
};

// Resize canvas to match window size
const resizeCanvas = () => {
  if (!waveCanvas.value) return;

  width = window.innerWidth;
  height = window.innerHeight;

  waveCanvas.value.width = width;
  waveCanvas.value.height = height;
};

// Animate waves
const animateWaves = (currentTime = 0) => {
  const deltaTime = currentTime - lastTime;
  lastTime = currentTime;

  // Clear canvas
  ctx.clearRect(0, 0, width, height);

  // Draw each wave
  waves.forEach(wave => {
    drawWave(wave, deltaTime);
  });

  // Continue animation loop
  animationFrameId = requestAnimationFrame(animateWaves);
};

// Draw a single wave
const drawWave = (wave, deltaTime) => {
  const { frequency, amplitude, speed, color, opacity, offset } = wave;

  // Update wave offset based on speed
  wave.offset += speed * 0.001 * deltaTime;

  ctx.beginPath();
  ctx.moveTo(0, height / 2);

  // Draw wave path
  for (let x = 0; x < width; x++) {
    const y = Math.sin(x * frequency + offset) * amplitude * height / 2 + height / 2;
    ctx.lineTo(x, y);
  }

  // Complete the path to fill the wave
  ctx.lineTo(width, height);
  ctx.lineTo(0, height);
  ctx.closePath();

  // Set wave color and opacity
  ctx.fillStyle = color;
  ctx.globalAlpha = opacity;
  ctx.fill();
  ctx.globalAlpha = 1;
};

// Função para formatar data em dd/mm/aaaa
const formatarData = (data) => {
  if (!data) return "Sem data";
  return new Date(data).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// Carregar dados da API ao montar
onMounted(async () => {
  try {
    const response = await fetch('/api/v1/news/articles/');
    const data = await response.json();
    informes.value = data;
  } catch (error) {
    console.error('Erro ao carregar informes:', error);
  }
  initWaveAnimation();


});

onUnmounted(() => {
  // Clean up animation
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId);
  }

  // Remove event listeners
  window.removeEventListener('resize', resizeCanvas);
  window.removeEventListener('keydown', trapFocus);

  // Restore body scroll
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.width = '';
  document.body.style.height = '';

  // Remove accessibility elements
  const skipLink = document.querySelector('a.sr-only');
  if (skipLink) skipLink.remove();

  const highContrastButton = document.querySelector('button[aria-label="Alternar modo de alto contraste"]');
  if (highContrastButton) highContrastButton.remove();
});



// Dados para as FAQs
const faqs = ref([
  {
    question: "Onde acessar o resultado de exames e biópsia ? Qual é o prazo para o exame ficar pronto?",
    answer: "O resultado do exame pode ser acessado aqui no site, em 'Resultado de Exames', utilizando login e senha ou realizando a leitura do QRCode do seu protocolo. O exame fica pronto em até 7 dias úteis. Caso seu exame tenha biópsia a mesma somente estará disponível em 15 dias, caso não consiga acessar o resultado da biópsia entrar em contato aqui no nosso botão azul de  'Fale Conosco'. Para a segurança do paciente não disponibilizamos resultado por e-mail e Whatsapp.",
    isOpen: false
  },
  {
    question: "Como ser atendido no Centro Carioca de Diagnóstico e Tratamento de Imagem?",
    answer: "Nós no Super Centro atendemos pacientes encaminhados via SISREG. Para ser inserido no sistema de regulação, você precisará ir à sua Clínica da Família e caso tenha indicação para o exame, o médico fará a inserção e você entrará na fila. Caso já tenha sido inserido no SISREG tem que aguardar sua vez na fila, quando o exame for marcado o agente comunitário da sua clínica da família irá informá-lo.",
    isOpen: false
  },
  {
    question: "Como solicitar e retirar lâmina?",
    answer: "A lâmina deverá ser solicitada no Centro Carioca de Diagnóstico e Tratamento de Imagem de 08h às 17h. A mesma só poderá ser solicitada e retirada pelo paciente, responsável legal (Tutor, com responsabilidade judicial). Nos casos de representantes (ex: esposa, filho) a pessoa precisa trazer o documento pessoal, o do paciente e o pedido médico solicitando o material. A retirada é feita em até 07 dias úteis após a solicitação de seg. a sex. de 08h às 16:30h",
    isOpen: false
  },
  {
    question: "O que é o Centro Carioca de Diagnóstico e Tratamento de Imagem?",
    answer: "O Centro Carioca de Diagnóstico e Tratamento de Imagem é responsável pela realização de exames de imagens como: Ressonância magnética, Tomografia, Mamografia, entre outros. Fazemos parte do complexo do Super Centro Carioca de Saúde.",
    isOpen: false
  },
  {
    question: "Onde fica localizado o Centro Carioca de Diagnóstico e Tratamento de Imagem?",
    answer: "Ficamos na Rua Dr. Rodrigues de Santana, 53 Bloco C - Benfica/RJ e funcionamos de segunda a sexta, das 7h às 20h e sábado, das 8h às 20h.\n\nFicamos próximo ao CADEG a aproximadamente 8 min da estação de trem/metrô de Triagem.",
    isOpen: false
  },
  {
    question: "Como posso remarcar um exame caso eu não tenha comparecido na data agendada?",
    answer: "Entrar em contato com o Centro Carioca de Diagnóstico e Tratamento de Imagem através do nosso WhatsApp, que nossa equipe de regulação verifica a possibilidade de remarcação.",
    isOpen: false
  },
  {
    question: "Como funciona o transporte gratuito disponibilizado pelo Centro Carioca?",
    answer: "A linha de ônibus exclusiva para transporte dos pacientes é circular, com paradas nos seguintes pontos:\n\n . Ponto inicial: Super Centro Carioca de Saúde – Centro Carioca de Especialidades e Centro Carioca do Olho (Hospital do Olho) (R. General Gustavo Cordeiro de Farias). Super Centro Carioca de Saúde – Centro Carioca de Diagnóstico (R. Doutor Rodrigues Santana) \n\n . INTO (Avenida Brasil – pista lateral, sentido Centro) . Estação São Cristóvão. Estação Triagem",
    isOpen: false
  },
  {
    question: "Como devo proceder se tiver dúvidas ou precisar de esclarecimentos após o exame?",
    answer: "Entre em contato com o nosso Whatsapp: (21) 99744-1613",
    isOpen: false
  },
]);

const toggleFaq = (index) => {
  faqs.value.forEach((faq, i) => {
    faq.isOpen = i === index ? !faq.isOpen : false;
  });
};

const scrollToNextSection = () => {
  // Pega todas as seções visíveis na página
  const sections = Array.from(document.querySelectorAll('section'))
    .filter(section => section.offsetParent !== null); // Apenas seções visíveis

  // Encontra a seção atual visível na tela
  const currentScroll = window.scrollY;

  // Encontra a próxima seção abaixo da posição atual
  const nextSection = sections.find(section => section.offsetTop > currentScroll + 50);

  if (nextSection) {
    nextSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  } else {
    console.warn('Nenhuma próxima seção encontrada.');
  }
};

onMounted(() => {
  const script = document.createElement('script');
  script.src = 'https://vlibras.gov.br/app/vlibras-plugin.js';
  script.onload = () => {
    new window.VLibras.Widget('https://vlibras.gov.br/app');
  };
  document.body.appendChild(script);
});

</script>

<style scoped>
.animate-fadeIn {
  animation: fadeIn 0.3s ease-in-out;
}

[vw] {
  z-index: 9999 !important;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.swiper-button-prev,
.swiper-button-next {
  color: #005baa;
  top: 50% !important;
  transform: translateY(-50%);
  z-index: 10;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 9999px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

/* Distanciamento lateral */
.swiper-button-prev {
  left: -20px;
}

.swiper-button-next {
  right: -20px;
}

/* Se preferir maior espaçamento externo do container */
@media (min-width: 768px) {
  .container {
    padding-left: 3rem;
    padding-right: 3rem;
  }
}

.wave-container {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
}


/* Certifique-se de que a formatação de conteúdo seja consistente com o fundo branco */
.prose {
  background-color: white; /* Garantir fundo branco */
  max-width: 100%; /* Permitir que o conteúdo ocupe toda a largura */
  border-radius: 15px;
}

.prose img {
  max-width: 100%;
  height: auto;
}

.prose pre {
  background-color: #f7f7f7;
  padding: 1rem;
  border-radius: 5px;
  color: #333;
}

.prose code {
  font-size: 1rem;
  background-color: #f0f0f0;
  border-radius: 15px;
  padding: 0.1rem 0.2rem;
  border-radius: 5px;
}

/* Ajustes para classes de indentação */
.ql-editor .ql-indent-1 {
  padding-left: 20px;
}

.ql-editor .ql-indent-2 {
  padding-left: 40px;
}

.ql-editor .ql-indent-3 {
  padding-left: 60px;
}

.ql-editor .ql-indent-4 {
  padding-left: 80px;
}
</style>



<script>
window.chatwootSettings = {"position":"right","type":"expanded_bubble","launcherTitle":"Duvidas ? Fale conosco!"};
  (function(d,t) {
    var BASE_URL="https://chat.ccdti.com.br";
    var g=d.createElement(t),s=d.getElementsByTagName(t)[0];
    g.src=BASE_URL+"/packs/js/sdk.js";
    g.async = true;
    s.parentNode.insertBefore(g,s);
    g.onload=function(){
      window.chatwootSDK.run({
        websiteToken: 'abmmBAVqESLNpfZFdmisgsg2',
        baseUrl: BASE_URL
      })
    }
  })(document,"script");
</script>
